/**
 * What a client sees when a component throws while rendering.
 *
 * Until now: nothing. React unmounts the whole tree on an unhandled render
 * error, so a single bad value in one field control took out the entire admin
 * and left a blank white page — no navigation, no message, no way back except
 * knowing to reload. For a panel a client uses on their own, that is the
 * difference between "this form is broken" and "the CMS is broken".
 *
 * It is used in two places, and the inner one is the one that earns its keep:
 *
 * - Around the whole app, so a failure in the shell itself still says
 *   something rather than showing a blank document.
 * - Around the routed content inside `Shell`, so the sidebar survives. A
 *   client whose speakers form throws can still click Programme and carry on
 *   working. Losing one screen is an inconvenience; losing the navigation is
 *   being stuck.
 *
 * Remounted on navigation by keying it on the location, because a boundary
 * that latches would show the error forever — the client clicks away, the
 * route changes, and the same message stays on screen.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router";

import { ErrorState } from "@/components/ui/states";

type Props = {
  children: ReactNode;
  /** What the reader can still do. Differs between the shell and one screen. */
  hint: string;
  onReset?: () => void;
};

type State = { error: Error | null };

class Boundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is where a developer will look, and it is the only place
    // the component stack exists — it is deliberately not put on screen. A
    // client reporting "it says TypeError: cannot read properties of
    // undefined" helps nobody, and a stack trace on a client's screen looks
    // like the system has come apart.
    console.error("Unhandled render error:", error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <ErrorState
        title="This screen could not be displayed"
        message={this.props.hint}
        onRetry={this.reset}
      />
    );
  }
}

/**
 * A boundary that forgets the error when the route changes.
 *
 * `key` on the location is the whole mechanism: React discards the instance
 * and its state, so navigating away is itself the recovery. Without it,
 * `getDerivedStateFromError` latches and every subsequent screen renders the
 * error the first one threw.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <Boundary
      key={location.pathname}
      hint="Something in this screen failed to render. The rest of the admin still works — try again, or pick another item from the sidebar."
    >
      {children}
    </Boundary>
  );
}

/** The outermost net. If this shows, the shell itself failed. */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Boundary
      hint="The admin failed to start. Reloading the page usually fixes it; if it does not, the error is in the browser console."
      onReset={() => window.location.reload()}
    >
      {children}
    </Boundary>
  );
}
